import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { TellentHttpApiService } from '@core/tellent-http-legacy';
import {
  AddOrgPayload,
  NewOrgResponse,
  Org,
  OrgDeletionStatus,
  OrgUpdate,
  TellentOrgAgreement,
  TellentOrgAgreementCheck,
  TellentOrgAgreementPayload,
} from '@core/tellent-types';

export type TellentOrganizationResponse = {
  org: Org;
};

@Injectable({ providedIn: 'root' })
export class TellentOrganizationHttpClient {
  protected readonly httpApi = inject(TellentHttpApiService);

  public fetch(): Observable<Org> {
    return this.httpApi
      .get('', null, null)
      .pipe(map((response: TellentOrganizationResponse) => response.org));
  }

  public update(body: OrgUpdate): Observable<Org> {
    const payload = this.shouldSendFormData(body)
      ? this.appendToFormData(new FormData(), body)
      : body;

    return this.httpApi
      .patch('', null, payload)
      .pipe(map((response: TellentOrganizationResponse) => response.org));
  }

  public delete(code: string): Observable<Org> {
    return this.httpApi
      .post('/request-deletion', null, { code })
      .pipe(map((response: TellentOrganizationResponse) => response.org));
  }

  public addOrg(addOrg: AddOrgPayload): Observable<NewOrgResponse> {
    return this.httpApi
      .post('/orgs', null, addOrg)
      .pipe(map((response: { org: NewOrgResponse }) => response.org));
  }

  public deletionStatus(): Observable<OrgDeletionStatus> {
    return this.httpApi
      .get('/deletion-request', null)
      .pipe(map((response: { data: OrgDeletionStatus }) => response.data));
  }

  public setAgreement(data: TellentOrgAgreementPayload): Observable<TellentOrgAgreement> {
    return this.httpApi
      .post('/agreements', null, data)
      .pipe(map((response: { data: TellentOrgAgreement }) => response.data));
  }

  public fetchAgreement(kind: string): Observable<TellentOrgAgreementCheck> {
    return this.httpApi
      .get(`/agreements/${kind}/check`, null)
      .pipe(map((response: { data: TellentOrgAgreementCheck }) => response.data));
  }

  private shouldSendFormData(data: Record<string, unknown>) {
    return Object.values(data).find(value => value instanceof File) !== undefined;
  }

  private appendToFormData(formData: FormData, data: Record<string, unknown>, parentKey?: string) {
    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined) return;

      const formKey = parentKey ? `${parentKey}[${key}]` : key;

      if (value instanceof File) {
        formData.append(formKey, value);
      } else if (value !== null && typeof value === 'object' && !(value instanceof Blob)) {
        this.appendToFormData(formData, <Record<string, unknown>>value, formKey);
      } else {
        formData.append(formKey, `${value}`);
      }
    });

    return formData;
  }
}
